import { PageEditor } from '@/components/admin/experiences/page-editor/page-editor';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { ApiError, fetchExperience } from '@/lib/api';
import { Spinner } from '@pms-digital/design-system';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * L'éditeur d'une page (refonte modulaire §9.2, §9.3; maquettes
 * `EditeurPageEcran` et `EditeurPageTelephone`; issue #58).
 *
 * ## Le `_` de `$experienceId_`, et pourquoi il porte l'écran
 *
 * TanStack's flat file routing nests `a.$b.c.tsx` UNDER `a.$b.tsx`, which would
 * make the editor of the experience the layout of this screen and render the
 * page editor inside its tabs - the exact opposite of §9.1, which puts it
 * « sans les trois onglets, il occupe l'écran seul ». The trailing `_` is the
 * router's own opt-out of that nesting: the path is unchanged
 * (`/admin/experiences/<id>/pages/<pageId>`) and the route is a sibling.
 *
 * ## Une seule requête, et la même clé que l'éditeur voisin
 *
 * `['experience', email, id]` holds the `ExperienceDetail` both screens read, on
 * purpose: the two edit ONE draft of ONE entity (§9.1), so a page renamed here
 * is the page the Timeline shows when one walks back. The trap of issue #16 is
 * two readers agreeing on a key and disagreeing on what is under it, which is
 * the opposite of this - same fetcher, same schema, same shape.
 *
 * There is no `?version=` here: « Voir » opens a version READ-ONLY, and reading
 * a version is a gesture of the experience's own editor, where the history
 * lives (§4). The breadcrumb is one click away.
 *
 * ## `key` sur l'éditeur, et ce qu'elle ne doit PAS contenir
 *
 * The same rule the sibling route states at length: the editor seeds its draft
 * from `detail` once, so « recharger le brouillon » is a REMOUNT - and keying
 * on `draft.updatedAt` would remount once a second while somebody types, since
 * every autosave answers a new stamp into this very query. The key is the id
 * and a COUNTER this screen owns.
 *
 * **La PAGE n'y est PAS**, et c'est la seconde MR de #58 qui l'a retirée. Elle y
 * était pour remettre à zéro la sélection, l'onglet de l'inspecteur et les
 * valeurs de répétition de l'aperçu en changeant de page - ce qui reste vrai, et
 * ce que `PageEditorBody` obtient maintenant par sa PROPRE `key` : tout cet
 * état-là vit chez lui. Ce qui ne vit pas chez lui, c'est le brouillon, et le
 * remonter en changeant de page le resemait depuis le serveur : « Insérer une
 * page après » ouvrait alors une page qui n'existait que dans la mémoire de
 * l'onglet, une seconde avant que l'autosauvegarde débouncée ne parte, et
 * l'écran répondait « cette page n'est plus dans la timeline » en jetant
 * l'insertion. Même perte, plus discrète, pour les dernières frappes d'un champ
 * quand on marchait vers la page voisine. Trouvé par `page-editor.spec.ts`.
 */

export const Route = createFileRoute('/admin/experiences/$experienceId_/pages/$pageId')({
  component: PageEditorPage,
});

function PageEditorPage() {
  const { email } = useAdminSession();
  const { experienceId, pageId } = Route.useParams();
  const navigate = useNavigate();
  /** Bumped by « Recharger le brouillon du serveur », and by nothing else. */
  const [reload, setReload] = useState(0);

  const experience = useQuery({
    queryKey: ['experience', email, experienceId],
    queryFn: () => fetchExperience(experienceId),
    retry: false,
  });

  const rejected = useRejectOn401(experience.error);
  const missing = experience.error instanceof ApiError && experience.error.status === 404;

  return (
    <div className="space-y-6">
      {experience.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner label="Chargement de la page" />
        </div>
      ) : null}

      {missing ? (
        <p className="text-gray-700">
          Cette expérience n’existe pas.{' '}
          <Link to="/admin/experiences" className="text-bleu-nuit underline underline-offset-2">
            Revenir à la bibliothèque
          </Link>
          .
        </p>
      ) : null}

      {experience.isError && !missing && !rejected ? (
        <p className="text-gray-700">Cette expérience n’a pas pu être chargée.</p>
      ) : null}

      {experience.data === undefined ? null : (
        <PageEditor
          key={`${experience.data.id}-${reload}`}
          detail={experience.data}
          pageId={pageId}
          onGoToPage={(next) =>
            void navigate({
              /*
                The ADDRESS and not the file name: the trailing `_` of
                `$experienceId_` is the router's opt-out of nesting and never
                reaches the path, so the typed route is spelled without it.
              */
              to: '/admin/experiences/$experienceId/pages/$pageId',
              params: { experienceId, pageId: next },
            })
          }
          onReload={() => {
            // The bump waits for the answer: a remount before it lands would
            // re-seed the editor from the stale draft it is escaping.
            void experience.refetch().then(() => setReload((was) => was + 1));
          }}
        />
      )}
    </div>
  );
}
